import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {
  ActorId,
  Defect,
  FlatRateUnit,
  FlatRateUnitGroup,
  IAuditSafeComment,
  IDefect,
  IFlatRateUnit,
  IFlatRateUnitGroup,
  IJob,
  IJobItems,
  IPackage,
  IPart,
  IPicturePanel,
  IRepairOverview,
  IServerApi,
  IServiceContract,
  ITechnicalDocument,
  Job,
  Package,
  Part,
  PicturePanel,
  RepairOverview,
  ServiceContract,
  TechnicalDocument,
  Topic
} from '@service-and-repairs/awpintegrationlib';
import {Customer, CustomerSource} from '@service-and-repairs/calm-leads-customer-lib-api';
import {Subject} from 'rxjs';
import {DmsCustomerDetermination} from '../../../components/dms-search/dms-customer-determination';
import {AwpClientLibService} from '../../../services/awp-client-lib.service';
import {CustomerLoader} from '../../customer/services/customer.loader';
import {CustomerService} from '../../customer/services/customer.service';
import {UserService} from '../../user/services/user.service';
import {ServiceCaseMessageKey} from '../enums/service-case-message-key';
import {ServiceCaseUpdateScope} from '../enums/service-case-update-scope';
import {ServiceCaseUpdateQueueService} from '../services/service-case-update-queue.service';
import {ServiceCaseService} from '../services/service-case.service';
import {ServiceCase} from './service-case';
import {ServiceCaseHolder} from './service-case-holder';
import {ServiceCaseUpdateError} from './service-case-update-error';
import {ServiceCaseUpdateLogger} from './service-case-update-logger';

const CUSTOMER_LIB_ORIGIN: string = 'customer lib';
const PREFERRED_CUSTOMER_MODIFICATION: string = 'setPreferredCustomer';
const DMS_LIB_ORIGIN: string = 'DMS lib';
const DETERMINE_DMS_CUSTOMER_MODIFICATION: string = 'determineSingleCustomerId';
const SHORTLIST_ORIGIN: string = 'Shortlist';
// Rejections arriving this soon after the active case was republished because of a rejection are treated as an echo
// of that republish and do not trigger another one, so an app republishing on every received case cannot loop.
const REJECTION_REPUBLISH_ECHO_WINDOW_IN_MS: number = 1000;
// Several modifications are often rejected at once (e.g. one per job). The user is notified only once per such burst.
const LOCKED_NOTIFICATION_DEDUPLICATION_WINDOW_IN_MS: number = 3000;

@Injectable({
  providedIn: 'root'
})
export class ServiceCaseUpdater {
  private readonly customerService: CustomerService = inject(CustomerService);
  private readonly customerLoader: CustomerLoader = inject(CustomerLoader);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly updateQueue: ServiceCaseUpdateQueueService = inject(ServiceCaseUpdateQueueService);
  private readonly userService: UserService = inject(UserService);
  private readonly serviceCaseUpdateLogger: ServiceCaseUpdateLogger = inject(ServiceCaseUpdateLogger);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);

  private readonly awpLib: IServerApi;

  private readonly serviceCaseService: ServiceCaseService = inject(ServiceCaseService);

  /**
   * Emits the external id of a service case that the backend reported as locked while a write was in flight.
   * Subscribers are expected to reload the service case so that the UI switches to read-only.
   */
  readonly serviceCaseLockedInBackend: Subject<string> = new Subject<string>();

  private lastRejectionRepublishTimestamp: number = 0;

  private lastLockedNotificationTimestamp: number = 0;

  constructor() {
    this.awpLib = this.awpClientLibService.serverApi;
    this.subscribeToMetadataUpdates();
    this.subscribeToUpdateJobs();
    this.subscribeToAddItems();
    this.subscribeToCustomerChanges();
    this.subscribeToDmsCustomerDeterminations();
  }

  saveServiceCaseMetadata(serviceCase: ServiceCase, origin: string): void {
    // A locked service case must never be written, not even by the enrichment saves the ServiceCaseManager triggers
    // after loading a service case. The backend rejects such a write with 409, which reloads the service case, and
    // the reload enriches it again and would request the very same write: an endless reload loop.
    // User triggered modifications are already rejected with a notification before they reach this point, so a
    // rejection here is a background save and is only logged.
    if (this.isLockedAgainstModification(serviceCase)) {
      console.warn('[Webapp] [Service Case Updater] Skipped the metadata save of the locked service case \''
        + serviceCase.getExternalId() + '\' requested by \'' + origin + '\'.');
      return;
    }
    this.logOriginOfBusinessValidationErrorsInServiceCaseMetadata(serviceCase, origin);
    serviceCase.setLastModifierUserName(this.userService.getCurrentUser().getName());
    // ATTENTION: A clone is required to prevent the service case that we want to
    // save from being changed (by the UI) while awaiting the backend response.
    const serviceCaseClone: ServiceCase = ServiceCase.fromPlainObject(serviceCase);
    const updatePromise: Promise<ServiceCase> = this.updateQueue.addMetadataUpdate(serviceCaseClone);
    this.handleUpdateServiceCaseResult(updatePromise, serviceCase.getExternalId(), serviceCase.getExternalId());
  }

  private saveServiceCaseJob(serviceCase: ServiceCase,
                             job: IJob,
                             origin: string,
                             auditSafeCommentsToPersist: IAuditSafeComment[] = []): void {
    this.logOriginOfBusinessValidationErrorsInJob(job, origin);
    // ATTENTION: A clone is required to prevent the service case that we want to
    // save from being changed (by the UI) while awaiting the backend response.
    const jobClone: IJob = Job.constructFromPlainObject(job);
    const updatePromise: Promise<IJob> = this.updateQueue.addJobUpdate(serviceCase, jobClone)
      // Audit-safe comments are stored independently of the job in the backend, so they must be persisted
      // separately once their parent position (the job or one of its items) exists in the backend.
      .then((persistedJob: IJob): IJob => {
        this.persistAuditSafeComments(auditSafeCommentsToPersist);
        return persistedJob;
      });
    this.handleUpdateServiceCaseResult(updatePromise, job.getExternalId(), serviceCase.getExternalId());
  }

  private persistAuditSafeComments(comments: IAuditSafeComment[]): void {
    comments.forEach((comment: IAuditSafeComment): void => {
      this.serviceCaseService.createAuditSafeComment(comment)
        // The backend assigns a new external id on creation, so keep the in-memory comment in sync with it.
        .then((persistedComment: IAuditSafeComment): void => comment.setExternalId(persistedComment.getExternalId()))
        .catch((error: any): void => {
          console.log('[Webapp] [ServiceCaseUpdater] Could not create audit-safe comment: ' + error.message);
          this.awpLib.notifyError(this.translate.instant('case.auditSafeCommentSaveError'));
        });
    });
  }

  private saveServiceCaseJobDeletion(serviceCase: ServiceCase, jobId: string): void {
    const updatePromise: Promise<void> = this.updateQueue.addJobDeletion(serviceCase, jobId);
    this.handleUpdateServiceCaseResult(updatePromise, jobId, serviceCase.getExternalId());
  }

  private handleUpdateServiceCaseResult(updatePromise: Promise<any>, updateId: string, serviceCaseId: string): void {
    const saveEventId: string = this.serviceCaseHolder.addServiceCaseIsBeingSavedEvent(updateId);
    updatePromise
      .then((): void => {
        // Nothing to do
      })
      .catch((reason: unknown): void => {
        if (ServiceCaseUpdateError.isServiceCaseLocked(reason)) {
          this.handleServiceCaseLockedInBackend(serviceCaseId);
          return;
        }
        this.handleUpdateErrors(updateId, ServiceCaseUpdateError.getMessage(reason));
      })
      .finally(() => this.serviceCaseHolder.removeServiceCaseIsBeingSavedEvent(updateId, saveEventId));
  }

  /**
   * Handles a write that the backend rejected because the service case was locked while the write was in flight.
   *
   * The local copy is outdated in that case: it still allows modifications although the backend no longer accepts
   * them. Reloading it is therefore the only way to get the UI into the read-only state the lock requires.
   *
   * @param serviceCaseId External id of the locked service case.
   */
  private handleServiceCaseLockedInBackend(serviceCaseId: string): void {
    console.warn('[Webapp] [Service Case Updater] '
      + 'Service case \'' + serviceCaseId + '\' is locked against modification. Reloading it.');
    // The local copy still contains the rejected modification. Marking it locked right away blocks every further
    // write of it, so the rejected modification cannot be saved after an unlock if the reload below fails.
    this.serviceCaseHolder.findByExternalId(serviceCaseId)?.setIsLocked(true);
    this.notifyLockedModificationRejected();
    this.serviceCaseLockedInBackend.next(serviceCaseId);
  }

  private applyUpdateToActiveServiceCase(updateScope: ServiceCaseUpdateScope, origin: string, job?: IJob, activeCase?: ServiceCase): void {
    activeCase = activeCase || this.serviceCaseHolder.getActiveCase();
    if (activeCase) {
      switch (updateScope) {
        case ServiceCaseUpdateScope.UPDATE_METADATA: // Service Case metadata will always be persisted
          break;
        case ServiceCaseUpdateScope.UPDATE_JOB: {
          const reassignedAuditSafeComments = activeCase.assignAuditSafeComments(job);
          this.saveServiceCaseJob(activeCase, job, origin, reassignedAuditSafeComments);
          break;
        }
        case ServiceCaseUpdateScope.DELETE_JOB:
          this.saveServiceCaseJobDeletion(activeCase, job.getExternalId());
          break;
      }
      // Service case metadata is also changed if jobs are added, updated or deleted because demand categories
      // may haven been changed.
      this.saveServiceCaseMetadata(activeCase, origin);
      this.serviceCaseHolder.onActiveCaseChanged();
    } else {
      this.awpLib.notifyError(this.translate.instant('case.noCaseToModify'));
    }
  }

  handleUpdateErrors(updateId: string, message: string): void {
    // Ignore errors, if more updates for the same service case are queued.
    console.error('[Webapp] [Service Case Updater] ' + message);
    if (!this.updateQueue.hasMoreWithSameId(updateId)) {
      this.awpLib.notifyError(this.translate.instant('case.notificationSaveError') + ' ' + message);
    }
  }

  private subscribeToCustomerChanges(): void {
    this.customerService.subscribeToSetPreferredCustomer((vin: string, customers: Customer[]): void => {
      if (this.rejectIfActiveCaseIsLocked(PREFERRED_CUSTOMER_MODIFICATION, CUSTOMER_LIB_ORIGIN)) {
        return;
      }
      const activeServiceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
      activeServiceCase.setCustomers({
        customers,
        dmsCustomerFurtherNotes: activeServiceCase.getCustomers().dmsCustomerFurtherNotes,
        dmsFurtherNotesCustomerId: activeServiceCase.getCustomers().dmsFurtherNotesCustomerId
      });
      if (activeServiceCase.getVinLong() === vin && activeServiceCase.setPreferredCustomer(customers)) {
        this.customerLoader.loadCustomerLanguageAndNumberInServiceCase(activeServiceCase)
          .finally((): void => this.saveServiceCaseMetadata(activeServiceCase, CUSTOMER_LIB_ORIGIN));
      }
      this.serviceCaseHolder.onActiveCaseChanged();
    });
  }

  /**
   * Apps may let the user select a single DMS customer via the DMS lib (determineSingleCustomerId). The selected
   * customer is set in the active service case, which is then saved and republished to all apps.
   */
  private subscribeToDmsCustomerDeterminations(): void {
    this.customerLoader.getDmsCustomerDeterminations()
      .subscribe(({vin, customerId}: DmsCustomerDetermination): void => {
        this.setDeterminedDmsCustomerInActiveCase(vin, customerId).catch((error: unknown): void =>
          console.error('[Webapp] [Service Case Updater] Could not set the DMS customer determined by the DMS lib.',
            error));
      });
  }

  private async setDeterminedDmsCustomerInActiveCase(vin: string, customerId: string): Promise<void> {
    const activeCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    if (!customerId
      || !activeCase
      || activeCase.getVinLong() !== vin
      || activeCase.getDmsCustomerNumber() === customerId) {
      return;
    }
    if (this.rejectIfActiveCaseIsLocked(DETERMINE_DMS_CUSTOMER_MODIFICATION, DMS_LIB_ORIGIN)) {
      return;
    }

    let customer: Customer = ServiceCaseUpdater.findCustomerByDmsId(activeCase, customerId);
    if (!customer) {
      // The determined customer might be unknown so far (e.g. created in the DMS just now).
      await this.customerLoader.loadCustomersInServiceCase(activeCase);
      customer = ServiceCaseUpdater.findCustomerByDmsId(activeCase, customerId);
    }

    // Must be set after reloading the customers, because the reload resets the DMS customer number to the preferred
    // customer. The customer explicitly determined via the DMS lib takes precedence over the preferred customer.
    activeCase.setDmsCustomerNumber(customerId);
    if (customer) {
      activeCase.updateCustomerName(customer.nameCollection);
      activeCase.setCustomerNameSource(customer.source ?? CustomerSource.DMS);
    } else {
      // Keeping the name of another customer would be misleading, so it is removed.
      console.warn('[Webapp] [Service Case Updater] DMS customer \'' + customerId
        + '\' determined by the DMS lib is not among the customers of the service case.');
      activeCase.updateCustomerName(undefined);
      activeCase.setCustomerNameSource(undefined);
    }

    this.saveServiceCaseMetadata(activeCase, DMS_LIB_ORIGIN);
    if (this.serviceCaseHolder.getActiveCase() === activeCase) {
      this.serviceCaseHolder.onActiveCaseChanged();
    }
  }

  private static findCustomerByDmsId(serviceCase: ServiceCase, dmsId: string): Customer {
    return serviceCase.getCustomers()?.customers
      ?.find((customer: Customer): boolean => customer?.idCollection?.dmsId === dmsId);
  }

  private subscribeToMetadataUpdates(): void {
    // Not routed through subscribeToModificationOfUnlockedActiveCase: metadata updates may target a service case
    // that is no longer the active one (INC000111238061), so the lock of that very service case has to be checked.
    this.awpLib.subscribeToActiveCaseModification(
      Topic.UPDATE_META_DATA,
      (plainServiceCase: any, origin: ActorId): void => {
        const publishedServiceCase: ServiceCase = ServiceCase.fromPlainObject(plainServiceCase);
        // apps might trigger updates for cases which are not longer the active ones (INC000111238061)
        const outdatedServiceCase: ServiceCase = this.serviceCaseHolder.findByExternalId(publishedServiceCase.getExternalId());
        if (this.isLockedAgainstModification(outdatedServiceCase ?? publishedServiceCase)) {
          this.rejectModificationOfLockedServiceCase(Topic.UPDATE_META_DATA, origin);
          return;
        }
        outdatedServiceCase?.updateMetaDataOnly(publishedServiceCase);
        this.applyUpdateToActiveServiceCase(ServiceCaseUpdateScope.UPDATE_METADATA, origin, undefined, publishedServiceCase);
      }
    );
  }

  /**
   * Subscribes to a modification of the active service case that another AWP app publishes, rejecting the
   * modification while the active service case is locked.
   *
   * Every mutating topic is registered through this method. Adding audit-safe comments is the one modification that
   * stays allowed on a locked service case and is therefore subscribed to directly. The public add and remove methods
   * check the lock themselves, because other webapp services (e.g. the search web component events) call them
   * directly.
   *
   * @param topic The topic to subscribe to.
   * @param onModification Handler applying the modification to the active service case.
   */
  private subscribeToModificationOfUnlockedActiveCase(topic: Topic,
                                                      onModification: (payload: any, origin: ActorId) => void): void {
    this.awpLib.subscribeToActiveCaseModification(topic, (payload: any, origin: ActorId): void => {
      if (this.rejectIfActiveCaseIsLocked(topic, origin)) {
        return;
      }
      onModification(payload, origin);
    });
  }

  /**
   * Rejects a modification of the active service case with a notification while the service case is locked.
   * Must be checked before the service case is changed, so that the UI never shows changes that cannot be saved.
   *
   * @param modification Name of the rejected modification, used for logging.
   * @param origin The app or component that requested the modification.
   * @returns true if the modification was rejected and must not be applied.
   */
  private rejectIfActiveCaseIsLocked(modification: string, origin: string): boolean {
    if (!this.isLockedAgainstModification(this.serviceCaseHolder.getActiveCase())) {
      return false;
    }
    this.rejectModificationOfLockedServiceCase(modification, origin);
    return true;
  }

  private isLockedAgainstModification(serviceCase: ServiceCase): boolean {
    return serviceCase?.getIsLocked() === true;
  }

  private rejectModificationOfLockedServiceCase(modification: string, origin: string): void {
    console.warn('[Webapp] [Service Case Updater] '
      + 'Rejected \'' + modification + '\' received from \'' + origin + '\': the service case is locked.');
    this.notifyLockedModificationRejected();
    this.republishActiveCaseAfterRejection();
  }

  private notifyLockedModificationRejected(): void {
    const now: number = Date.now();
    if (now - this.lastLockedNotificationTimestamp < LOCKED_NOTIFICATION_DEDUPLICATION_WINDOW_IN_MS) {
      return;
    }
    this.lastLockedNotificationTimestamp = now;
    this.awpLib.notifyError(this.translate.instant(ServiceCaseMessageKey.LOCKED_MODIFICATION_REJECTED));
  }

  /**
   * Republishes the unchanged active service case, so that apps which already applied the rejected modification to
   * their own copy of the service case discard it again.
   */
  private republishActiveCaseAfterRejection(): void {
    const now: number = Date.now();
    if (now - this.lastRejectionRepublishTimestamp < REJECTION_REPUBLISH_ECHO_WINDOW_IN_MS) {
      return;
    }
    this.lastRejectionRepublishTimestamp = now;
    this.awpLib.publishActiveCase(this.serviceCaseHolder.getActiveCase());
  }

  private subscribeToUpdateJobs(): void {
    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_EMPTY_JOB,
      (_emptyPayload: null, origin: ActorId): void => {
        this.showItemsAddedToastMessage(Topic.ADD_EMPTY_JOB);
        const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
        const job: IJob = serviceCase.addEmptyJobAsFirstJobInBasket(this.translate.instant('case.newJob'));
        this.serviceCaseUpdateLogger.logAddedJobs([job], serviceCase);
        // update all jobs as their position numbers have changed
        serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
          ServiceCaseUpdateScope.UPDATE_JOB,
          origin,
          j
        ));
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_EMPTY_CONSULTATION_JOB,
      (_emptyPayload: null, origin: ActorId): void => {
        this.showItemsAddedToastMessage(Topic.ADD_EMPTY_CONSULTATION_JOB);
        const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
        const job: IJob = serviceCase.addEmptyJobAsFirstJobInConsultationList(this.translate.instant('case.newJob'));
        this.serviceCaseUpdateLogger.logAddedJobs([job], serviceCase);
        // update all jobs as their position numbers have changed
        serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
          ServiceCaseUpdateScope.UPDATE_JOB,
          origin,
          j
        ));
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_CONSULTATION_JOBS,
      (plainJobs: any[], origin: ActorId): void => {
        this.showItemsAddedToastMessage(Topic.ADD_CONSULTATION_JOBS);
        const jobs: IJob[] = plainJobs.map((plainJob: any): IJob => Job.constructFromPlainObject(plainJob));
        const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
        const addedJobs: IJob[] = jobs.map((job: IJob): IJob => {
            const addedJob: IJob = serviceCase.addJob(job, true);
            this.applyUpdateToActiveServiceCase(ServiceCaseUpdateScope.UPDATE_JOB, origin, addedJob);
            return addedJob;
          }
        );
        this.serviceCaseUpdateLogger.logAddedJobs(addedJobs, serviceCase);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(Topic.ADD_JOBS, (plainJobs: any[], origin: ActorId): void => {
      this.showItemsAddedToastMessage(Topic.ADD_JOBS);
      const jobs: IJob[] = plainJobs.map((plainJob: any): IJob => Job.constructFromPlainObject(plainJob));
      const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
      const addedJobs: IJob[] = jobs.map((job: IJob): IJob => {
          const addedJob: IJob = serviceCase.addJob(job, false);
          this.applyUpdateToActiveServiceCase(ServiceCaseUpdateScope.UPDATE_JOB, origin, addedJob);
          return addedJob;
        }
      );
      this.serviceCaseUpdateLogger.logAddedJobs(addedJobs, serviceCase);
    });

    this.subscribeToModificationOfUnlockedActiveCase(Topic.UPDATE_JOB, (plainJob: any, origin: ActorId): void => {
      const updatedJob: IJob = Job.constructFromPlainObject(plainJob);
      const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
      const job: IJob = serviceCase.getAllJobs().find((j: IJob): boolean =>
        j.getExternalId() === updatedJob.getExternalId());
      this.serviceCaseUpdateLogger.logUpdatedJob(updatedJob, job, serviceCase);
      this.applyUpdateToActiveServiceCase(ServiceCaseUpdateScope.UPDATE_JOB, origin, serviceCase.updateJob(updatedJob));
    });

    this.subscribeToModificationOfUnlockedActiveCase(Topic.REMOVE_JOBS, (plainJobs: any[], _origin: ActorId): void => {
      const jobs: IJob[] = plainJobs.map((plainJob: any): IJob => Job.constructFromPlainObject(plainJob));
      this.removeJobsFromServiceCase(jobs);
    });
  }

  private subscribeToAddItems(): void {
    this.awpLib.subscribeToAddedAuditSafeComment((comment: IAuditSafeComment, _origin: ActorId): void => {
      this.serviceCaseService.createAuditSafeComment(comment)
        .then((comment: IAuditSafeComment): void => {
          this.serviceCaseHolder.getActiveCase().addAuditSafeComment(comment);
          this.serviceCaseHolder.onActiveCaseChanged();
        })
        .catch((error: any): void =>
          console.log('[Webapp] [ServiceCaseUpdater] Could not create audit-safe comment: ' + error.message));
    });

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_DEFECTS,
      (plainItems: any[], origin: ActorId): void => {
        const defects: IDefect[] = plainItems.map((plainItem: any): IDefect =>
          Defect.constructFromPlainObject(plainItem));
        this.addDefects(defects, origin);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_FLAT_RATE_UNITS,
      (plainItems: any[], origin: ActorId): void => {
        const flatRateUnits: IFlatRateUnit[] = plainItems.map((plainItem: any): IFlatRateUnit =>
          FlatRateUnit.constructFromPlainObject(plainItem));
        this.addFlatRateUnits(flatRateUnits, origin);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(Topic.ADD_PARTS, (plainItems: any[], origin: ActorId): void => {
      const parts: IPart[] = plainItems.map((plainItem: any): IPart => Part.constructFromPlainObject(plainItem));
      this.addParts(parts, origin);
    });

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_PARTS_TO_JOB,
      (jobItems: IJobItems<IPart>, origin: ActorId): void => {
        const parts: IPart[] = jobItems.items.map((plainItem: IPart): IPart =>
          Part.constructFromPlainObject(plainItem));
        this.addPartsToJob(jobItems.jobId, parts, origin);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_FLAT_RATE_UNITS_TO_JOB,
      (jobItems: IJobItems<IFlatRateUnit>, origin: ActorId): void => {
        const flatRateUnits: IFlatRateUnit[] = jobItems.items.map((plainItem: IFlatRateUnit): IFlatRateUnit =>
          FlatRateUnit.constructFromPlainObject(plainItem));
        this.addFlatRateUnitsToJob(jobItems.jobId, flatRateUnits, origin);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(Topic.ADD_PACKAGES, (plainItems: any[], origin: ActorId): void => {
      const packages: IPackage[] = plainItems.map((plainItem: any): IPackage =>
        Package.constructFromPlainObject(plainItem));
      this.addPackages(packages, origin);
    });

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_FLAT_RATE_UNIT_GROUP,
      (plainItem: any, origin: ActorId): void => {
        const flatRateUnitGroup: IFlatRateUnitGroup = FlatRateUnitGroup.constructFromPlainObject(plainItem);
        this.addFlatRateUnitGroup(flatRateUnitGroup, origin);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_PICTURE_PANEL,
      (plainItem: any, origin: ActorId): void => {
        const picturePanel: IPicturePanel = PicturePanel.constructFromPlainObject(plainItem);
        this.addPicturePanel(picturePanel, origin);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_REPAIR_OVERVIEW,
      (plainItem: any, origin: ActorId): void => {
        const repairOverview: IRepairOverview = RepairOverview.constructFromPlainObject(plainItem);
        this.addRepairOverview(repairOverview, origin);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_TECHNICAL_DOCUMENT,
      (plainItem: any, origin: ActorId): void => {
        const technicalDocument: ITechnicalDocument = TechnicalDocument.constructFromPlainObject(plainItem);
        this.addTechnicalDocument(technicalDocument, origin);
      }
    );

    this.subscribeToModificationOfUnlockedActiveCase(
      Topic.ADD_SERVICE_CONTRACT,
      (plainServiceContract: any, origin: ActorId): void => {
        const serviceContract: IServiceContract = ServiceContract.constructFromPlainObject(plainServiceContract);
        this.addServiceContract(serviceContract, origin);
      }
    );
  }

  addTechnicalDocument(technicalDocument: ITechnicalDocument, origin: string): void {
    if (this.rejectIfActiveCaseIsLocked(Topic.ADD_TECHNICAL_DOCUMENT, origin)) {
      return;
    }
    this.showItemsAddedToastMessage(Topic.ADD_TECHNICAL_DOCUMENT);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    serviceCase.addTechnicalDocument(technicalDocument);
    // update all jobs as their position numbers may have changed, if a new empty job was added at the beginning
    serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
      ServiceCaseUpdateScope.UPDATE_JOB,
      origin,
      j
    ));
  }

  private addRepairOverview(repairOverview: IRepairOverview, origin: string): void {
    this.showItemsAddedToastMessage(Topic.ADD_REPAIR_OVERVIEW);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const job: IJob = serviceCase.addRepairOverview(repairOverview);
    this.serviceCaseUpdateLogger.logAddedFlatRateUnits(
      repairOverview.getFlatRateUnitGroups()
        .flatMap((flatRateUnitGroup: IFlatRateUnitGroup): IFlatRateUnit[] => flatRateUnitGroup.getFlatRateUnits()),
      job,
      serviceCase
    );
    this.serviceCaseUpdateLogger.logAddedParts(
      repairOverview.getPicturePanels().flatMap((picturePanel: IPicturePanel): IPart[] => picturePanel.getParts()),
      job,
      serviceCase
    );
    this.serviceCaseUpdateLogger.logAddedDefects(
      [repairOverview.getDefect()],
      job,
      serviceCase
    );
    // update all jobs as their position numbers may have changed, if a new empty job was added at the beginning
    serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
      ServiceCaseUpdateScope.UPDATE_JOB,
      origin,
      j
    ));
  }

  private addPicturePanel(picturePanel: IPicturePanel, origin: string): void {
    this.showItemsAddedToastMessage(Topic.ADD_PICTURE_PANEL);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const job: IJob = serviceCase.addPicturePanel(picturePanel, this.translate.instant('case.newJob'));
    this.serviceCaseUpdateLogger.logAddedParts(
      picturePanel.getParts(),
      job,
      serviceCase
    );
    // update all jobs as their position numbers may have changed, if a new empty job was added at the beginning
    serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
      ServiceCaseUpdateScope.UPDATE_JOB,
      origin,
      j
    ));
  }

  private addFlatRateUnitGroup(flatRateUnitGroup: IFlatRateUnitGroup, origin: string): void {
    this.showItemsAddedToastMessage(Topic.ADD_FLAT_RATE_UNIT_GROUP);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const job: IJob = serviceCase.addFlatRateUnitGroup(flatRateUnitGroup);
    this.serviceCaseUpdateLogger.logAddedFlatRateUnits(
      flatRateUnitGroup.getFlatRateUnits(),
      job,
      serviceCase
    );
    // update all jobs as their position numbers may have changed, if a new empty job was added at the beginning
    serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
      ServiceCaseUpdateScope.UPDATE_JOB,
      origin,
      j
    ));
  }

  addPackages(packages: IPackage[], origin: string): void {
    if (this.rejectIfActiveCaseIsLocked(Topic.ADD_PACKAGES, origin)) {
      return;
    }
    this.showItemsAddedToastMessage(Topic.ADD_PACKAGES);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    packages.forEach((pack: IPackage): void => {
        const job: IJob = serviceCase.addOrReplacePackage(pack);
        this.serviceCaseUpdateLogger.logAddedPackage(pack, job, serviceCase);
        this.applyUpdateToActiveServiceCase(ServiceCaseUpdateScope.UPDATE_JOB, origin, job);
      }
    );
  }

  addServiceContract(serviceContract: IServiceContract, origin: string): void {
    if (this.rejectIfActiveCaseIsLocked(Topic.ADD_SERVICE_CONTRACT, origin)) {
      return;
    }
    this.showItemsAddedToastMessage(Topic.ADD_SERVICE_CONTRACT);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const job: IJob = serviceCase.addOrReplaceServiceContract(serviceContract);
    this.applyUpdateToActiveServiceCase(ServiceCaseUpdateScope.UPDATE_JOB, origin, job);
  }

  addDefects(defects: IDefect[], origin: string): void {
    if (this.rejectIfActiveCaseIsLocked(Topic.ADD_DEFECTS, origin)) {
      return;
    }
    this.showItemsAddedToastMessage(Topic.ADD_DEFECTS);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const job: IJob = serviceCase.addDefects(defects);
    this.serviceCaseUpdateLogger.logAddedDefects(defects, job, serviceCase);
    // update all jobs as their position numbers may have changed, if a new empty job was added at the beginning
    serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
      ServiceCaseUpdateScope.UPDATE_JOB,
      origin,
      j
    ));
  }

  addFlatRateUnits(flatRateUnits: IFlatRateUnit[], origin: string): void {
    if (this.rejectIfActiveCaseIsLocked(Topic.ADD_FLAT_RATE_UNITS, origin)) {
      return;
    }
    this.showItemsAddedToastMessage(Topic.ADD_FLAT_RATE_UNITS);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const job: IJob = serviceCase.addFlatRateUnits(flatRateUnits);
    this.serviceCaseUpdateLogger.logAddedFlatRateUnits(flatRateUnits, job, serviceCase);
    // update all jobs as their position numbers may have changed, if a new empty job was added at the beginning
    serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
      ServiceCaseUpdateScope.UPDATE_JOB,
      origin,
      j
    ));
  }

  addParts(parts: IPart[], origin: string): void {
    if (this.rejectIfActiveCaseIsLocked(Topic.ADD_PARTS, origin)) {
      return;
    }
    this.showItemsAddedToastMessage(Topic.ADD_PARTS);
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const job: IJob = serviceCase.addParts(parts);
    this.serviceCaseUpdateLogger.logAddedParts(parts, job, serviceCase);
    // update all jobs as their position numbers may have changed, if a new empty job was added at the beginning
    serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
      ServiceCaseUpdateScope.UPDATE_JOB,
      origin,
      j
    ));
  }

  addPartsToJob(jobId: string, parts: IPart[], origin: string): void {
    this.addItemsToJob(
      Topic.ADD_PARTS,
      Topic.ADD_PARTS_TO_JOB,
      jobId,
      origin,
      (serviceCase: ServiceCase): IJob | null => serviceCase.addPartsToJob(jobId, parts),
      (job: IJob, serviceCase: ServiceCase): void =>
        this.serviceCaseUpdateLogger.logAddedParts(parts, job, serviceCase)
    );
  }

  addFlatRateUnitsToJob(jobId: string, flatRateUnits: IFlatRateUnit[], origin: string): void {
    this.addItemsToJob(
      Topic.ADD_FLAT_RATE_UNITS,
      Topic.ADD_FLAT_RATE_UNITS_TO_JOB,
      jobId,
      origin,
      (serviceCase: ServiceCase): IJob | null => serviceCase.addFlatRateUnitsToJob(jobId, flatRateUnits),
      (job: IJob, serviceCase: ServiceCase): void =>
        this.serviceCaseUpdateLogger.logAddedFlatRateUnits(flatRateUnits, job, serviceCase)
    );
  }

  private addItemsToJob(
    topic: Topic,
    modification: Topic,
    jobId: string,
    origin: string,
    addItems: (serviceCase: ServiceCase) => IJob | null,
    logAddedItems: (job: IJob, serviceCase: ServiceCase) => void
  ): void {
    if (this.rejectIfActiveCaseIsLocked(modification, origin)) {
      return;
    }
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const job: IJob | null = addItems(serviceCase);
    if (!job) {
      console.warn(
        '[Webapp] [ServiceCaseUpdater] Could not add items from \'' + origin + '\' to unknown job \'' + jobId + '\'.'
      );
      return;
    }

    this.showItemsAddedToastMessage(topic);
    logAddedItems(job, serviceCase);
    this.applyUpdateToActiveServiceCase(ServiceCaseUpdateScope.UPDATE_JOB, origin, job);
  }

  removeJobsFromServiceCase(jobs: IJob[]): void {
    if (this.rejectIfActiveCaseIsLocked(Topic.REMOVE_JOBS, SHORTLIST_ORIGIN)) {
      return;
    }
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    jobs.forEach((job: IJob): void => {
      const deletedJob: IJob = serviceCase.removeJob(job);
      this.serviceCaseUpdateLogger.logDeletedJob(deletedJob, serviceCase);
      this.applyUpdateToActiveServiceCase(ServiceCaseUpdateScope.DELETE_JOB, SHORTLIST_ORIGIN, deletedJob);
    });
    // update all remaining jobs as their position numbers may have changed
    serviceCase.getAllJobs().forEach((j: IJob): void => this.applyUpdateToActiveServiceCase(
      ServiceCaseUpdateScope.UPDATE_JOB,
      SHORTLIST_ORIGIN,
      j
    ));
  }

  showItemsAddedToastMessage(topic: Topic): void {
    if (!window.location.pathname.includes('caseoverview') && !window.location.pathname.includes('casedetails')) {
      let key: string = '';
      switch (topic) {
        case Topic.ADD_DEFECTS:
          key = 'common.addedDefects';
          break;
        case Topic.ADD_FLAT_RATE_UNITS:
          key = 'common.addedFlatRateUnits';
          break;
        case Topic.ADD_PARTS:
          key = 'common.addedParts';
          break;
        case Topic.ADD_PACKAGES:
          key = 'common.addedPackages';
          break;
        case Topic.ADD_FLAT_RATE_UNIT_GROUP:
          key = 'common.addedFlatRateUnitGroup';
          break;
        case Topic.ADD_PICTURE_PANEL:
          key = 'common.addedPicturePanel';
          break;
        case Topic.ADD_REPAIR_OVERVIEW:
          key = 'common.addedRepairOverview';
          break;
        case Topic.ADD_TECHNICAL_DOCUMENT:
          key = 'common.addedTechnicalDocument';
          break;
        case Topic.ADD_SERVICE_CONTRACT:
          key = 'common.addedServiceContract';
          break;
        case Topic.ADD_CONSULTATION_JOBS:
        case Topic.ADD_JOBS:
        case Topic.ADD_EMPTY_CONSULTATION_JOB:
        case Topic.ADD_EMPTY_JOB:
          key = 'common.addedJobs';
          break;
      }
      const isConsultationList: boolean = this.isTargetConsultationList(topic);
      const targetKey: string = isConsultationList ? 'case.consultationList' : 'case.basket';
      const itemMessage: string = this.translate.instant(key);
      const targetMessage: string = this.translate.instant(targetKey);
      this.awpLib.notifySuccess(`${itemMessage} (${targetMessage})`);
    }
  }

  private isTargetConsultationList(topic: Topic): boolean {
    if (topic === Topic.ADD_CONSULTATION_JOBS || topic === Topic.ADD_EMPTY_CONSULTATION_JOB) {
      return true;
    }
    if (topic === Topic.ADD_JOBS || topic === Topic.ADD_EMPTY_JOB) {
      return false;
    }
    const serviceCase: ServiceCase = this.serviceCaseHolder.getActiveCase();
    const activeJob: IJob = serviceCase.getActiveJob();
    return serviceCase.getConsultationJobs()
      .some((job: IJob): boolean => job.getExternalId() === activeJob.getExternalId());
  }

  // TODO: remove once alls apps fixed the fru type and the validation in the backend was adjusted
  private logOriginOfBusinessValidationErrorsInFlatRateUnits(flatRateUnits: IFlatRateUnit[],
                                                             jobId: string,
                                                             origin: string): void {
    flatRateUnits?.forEach((fru: IFlatRateUnit): void => {
      if (fru.getDescription().trim().length === 0) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Empty description for flat rate unit \'' + fru.getNumber() + '\''
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
      if (!['AW', 'AZ'].includes(fru.getType())) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Unknown flat rate unit type \'' + fru.getType() + '\''
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
      if (fru.getGrossPrice() < 0) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Invalid price \'' + fru.getGrossPrice() + '\' for flat rate unit \'' + fru.getNumber() + '\''
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
      if (fru.getIsLocal() === undefined || fru.getIsLocal() === null) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Mandatory flag \'isLocal\' is missing for flat rate unit \'' + fru.getNumber() + '\''
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
      if (!fru.getIsLocal() && fru.getNumber().trim().length !== 7) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Invalid number for flat rate unit \'' + fru.getNumber() + '\' (isLocal=' + fru.getIsLocal() + ')'
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
    });
  }

  // TODO: remove once alls apps fixed the fru type and the validation in the backend was adjusted
  private logOriginOfBusinessValidationErrorsInParts(parts: IPart[], jobId: string, origin: string): void {
    parts?.forEach((part: IPart): void => {
      if (part.getDescription().trim().length === 0) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Empty description for part \'' + part.getPartNumber() + '\''
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
      if (!part.getIsLocal() && part.getPartNumber().trim().length !== 11) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Invalid part number for part \'' + part.getPartNumber() + '\''
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
      if (part.getTotalCustomerGrossPrice() < 0) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Invalid price \'' + part.getTotalCustomerGrossPrice() + '\' for part \'' + part.getPartNumber() + '\''
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
      if (part.getIsLocal() === undefined || part.getIsLocal() === null) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Mandatory flag \'isLocal\' is missing for part \'' + part.getPartNumber() + '\''
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
      if (!part.getIsLocal() && part.getPartNumber().trim().length !== 11) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Invalid part number for part \'' + part.getPartNumber() + '\' (isLocal=' + part.getIsLocal() + ')'
          + ' in job \'' + jobId + '\''
          + ' received from \'' + origin + '\'.');
      }
    });
  }

  // TODO: remove once alls apps fixed the fru type and the validation in the backend was adjusted
  private logOriginOfBusinessValidationErrorsInPackages(pack: IPackage, jobId: string, origin: string): void {
    this.logOriginOfBusinessValidationErrorsInFlatRateUnits(pack?.getFlatRateUnits(), jobId, origin);
    this.logOriginOfBusinessValidationErrorsInParts(pack?.getParts(), jobId, origin);

    if (pack?.getDefects().length > 1) {
      console.warn('[Webapp] [Service Case Updater] '
        + 'Invalid amount of defects (' + pack.getDefects().length + ')'
        + ' in package \'' + pack.getNumber() + '\''
        + ' in job \'' + jobId + '\''
        + ' received from \'' + origin + '\'.');
    }
    if (pack && !pack.getIsLocal() && pack.getNumber().length !== 7) {
      console.warn('[Webapp] [Service Case Updater] '
        + 'Invalid package number for package \'' + pack.getNumber() + '\''
        + ' in job \'' + jobId + '\''
        + ' received from \'' + origin + '\'.');
    }
    if (pack && !pack.getIsLocal() && pack.getNumber().trim().length !== 7) {
      console.warn('[Webapp] [Service Case Updater] '
        + 'Invalid package number for package \'' + pack.getNumber() + '\' (isLocal=' + pack.getIsLocal() + ')'
        + ' in job \'' + jobId + '\''
        + ' received from \'' + origin + '\'.');
    }
  }

  // TODO: remove once alls apps fixed the fru type and the validation in the backend was adjusted
  private logOriginOfBusinessValidationErrorsInJob(job: IJob, origin: string): void {
    if (job?.getTitle()?.trim().length < 3) {
      console.warn('[Webapp] [Service Case Updater] '
        + 'Invalid title \'' + job.getTitle().trim() + '\''
        + ' in job \'' + job.getExternalId() + '\''
        + ' received from \'' + origin + '\'.');
    }

    job?.getDemandIds()?.forEach((demandId: string): void => {
      if (demandId.trim().length === 0) {
        console.warn('[Webapp] [Service Case Updater] '
          + 'Empty demand id'
          + ' in job \'' + job.getExternalId() + '\''
          + ' received from \'' + origin + '\'.');
      }
    });

    const frus: IFlatRateUnit[] = job.getFlatRateUnits()
      .concat(job.getFlatRateUnitGroups()
        .flatMap((group: IFlatRateUnitGroup): IFlatRateUnit[] => group.getFlatRateUnits()))
      .concat(job.getRepairOverviews()
        .flatMap((overview: RepairOverview): IFlatRateUnit[] => overview.getFlatRateUnitGroups()
          .flatMap((group: IFlatRateUnitGroup): IFlatRateUnit[] => group.getFlatRateUnits())));
    this.logOriginOfBusinessValidationErrorsInFlatRateUnits(frus, job?.getExternalId(), origin);

    const parts: IPart[] = job.getParts()
      .concat(job.getPicturePanels().flatMap((panel: IPicturePanel): IPart[] => panel.getParts()))
      .concat(job.getRepairOverviews().flatMap((overview: RepairOverview): IPart[] => overview.getPicturePanels()
        .flatMap((panel: IPicturePanel): IPart[] => panel.getParts())));
    this.logOriginOfBusinessValidationErrorsInParts(parts, job?.getExternalId(), origin);

    this.logOriginOfBusinessValidationErrorsInPackages(job?.getPackages().at(0), job?.getExternalId(), origin);
  }

  // TODO: remove once alls apps fixed the fru type and the validation in the backend was adjusted
  private logOriginOfBusinessValidationErrorsInServiceCaseMetadata(serviceCase: ServiceCase, origin: string): void {
    if (serviceCase?.getTitle()?.trim().length === 0) {
      console.warn('[Webapp] [Service Case Updater] '
        + 'Empty title in service case \'' + serviceCase.getExternalId() + '\''
        + ' received from \'' + origin + '\'.');
    }
    if (serviceCase?.getCustomerName()?.length === 0) {
      console.warn('[Webapp] [Service Case Updater] '
        + 'Empty customer name in service case \'' + serviceCase.getExternalId() + '\''
        + ' received from \'' + origin + '\'.');
    }
    if (serviceCase?.getLicensePlate()?.length > 16) {
      console.warn('[Webapp] [Service Case Updater] '
        + 'Invalid license plate \'' + serviceCase.getLicensePlate() + '\''
        + ' in service case \'' + serviceCase.getExternalId() + '\''
        + ' received from \'' + origin + '\'.');
    }
    if (serviceCase?.getCurrentMileageInKilometers() > 9999999) {
      console.warn('[Webapp] [Service Case Updater] '
        + 'Invalid mileage \'' + serviceCase.getMileage() + '\''
        + ' in service case \'' + serviceCase.getExternalId() + '\''
        + ' received from \'' + origin + '\'.');
    }
  }
}
